import { useState, useEffect } from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  SimpleGrid,
  Tooltip,
  Progress,
} from "@chakra-ui/react";

const BIRTH_DATE = new Date(2025, 0, 10); // 10 January 2025
const AGE_18_DATE = new Date(2043, 0, 10); // 18th birthday — ținta proiectului ($500/lună)
const DAY_MS = 1000 * 60 * 60 * 24;

// Fapte fixe legate de data nașterii
const BORN_WEEKDAY = BIRTH_DATE.toLocaleDateString("en-US", { weekday: "long" }); // Friday
const ZODIAC = { name: "Capricorn", symbol: "♑" }; // 10 Jan → Capricorn

const calculateAge = () => {
  const now = new Date();
  let years = now.getFullYear() - BIRTH_DATE.getFullYear();
  let months = now.getMonth() - BIRTH_DATE.getMonth();
  let days = now.getDate() - BIRTH_DATE.getDate();

  if (days < 0) {
    months -= 1;
    days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const totalDays = Math.floor((now - BIRTH_DATE) / DAY_MS);
  const totalWeeks = Math.floor(totalDays / 7);
  const totalMonths = years * 12 + months;

  const nextBirthday = new Date(now.getFullYear(), 0, 10);
  if (nextBirthday <= now) nextBirthday.setFullYear(now.getFullYear() + 1);
  const daysUntilBirthday = Math.ceil((nextBirthday - now) / DAY_MS);

  // Drumul către 18 ani (ținta de $500/lună până la majorat)
  const totalDaysTo18 = Math.round((AGE_18_DATE - BIRTH_DATE) / DAY_MS);
  const daysUntil18 = Math.max(0, Math.ceil((AGE_18_DATE - now) / DAY_MS));
  const progressTo18 = Math.min(100, (totalDays / totalDaysTo18) * 100);

  return {
    years,
    months,
    days,
    totalDays,
    totalWeeks,
    totalMonths,
    daysUntilBirthday,
    daysUntil18,
    progressTo18,
  };
};




// ─── Sub-components ──────────────────────────────────────────
const AgeCard = ({ value, label, emoji, color }) => (
  <Box
    bg="whiteAlpha.100"
    border="1px solid"
    borderColor={`${color}.700`}
    borderRadius={{ base: "lg", md: "xl" }}
    px={{ base: 4, md: 6 }}
    py={{ base: 2, md: 3 }}
    textAlign="center"
    transition="all 0.3s"
    _hover={{
      transform: "translateY(-3px)",
      borderColor: `${color}.500`,
    }}
  >
    <Text
      fontSize={{ base: "2xl", md: "4xl" }}
      fontWeight="black"
      color={`${color}.300`}
      lineHeight="1"
    >
      {value}
    </Text>
    <Text
      fontSize={{ base: "2xs", md: "xs" }}
      color="gray.400"
      fontWeight="bold"
      mt={1}
      textTransform="uppercase"
      letterSpacing="wider"
    >
      {label} {emoji}
    </Text>
  </Box>
);

const FunStat = ({ emoji, label, value, tooltip, color }) => (
  <Tooltip label={tooltip} hasArrow placement="top">
    <Box
      bg="whiteAlpha.50"
      border="1px solid"
      borderColor="whiteAlpha.100"
      borderRadius="lg"
      px={{ base: 2, md: 3 }}
      py={{ base: 1.5, md: 2 }}
      textAlign="center"
      cursor="default"
      transition="all 0.2s"
      _hover={{ bg: "whiteAlpha.100", transform: "scale(1.05)" }}
    >
      <Text fontSize={{ base: "sm", md: "md" }}>{emoji}</Text>
      <Text
        fontSize={{ base: "sm", md: "md" }}
        fontWeight="bold"
        color={`${color}.300`}
      >
        {value}
      </Text>
      <Text fontSize="2xs" color="gray.500">
        {label}
      </Text>
    </Box>
  </Tooltip>
);

// ─── Main Component ──────────────────────────────────────────
const MattiasAgeTracker = () => {
  const [age, setAge] = useState(calculateAge);

  useEffect(() => {
    const interval = setInterval(() => setAge(calculateAge()), 86400000);
    return () => clearInterval(interval);
  }, []);


  return (
    <Box
      maxW="900px"
      mx="auto"
      textAlign="center"
      py={{ base: 3, md: 5 }}
      px={{ base: 2, md: 4 }}
    >
      <VStack spacing={{ base: 3, md: 4 }}>
        {/* Title */}
        <Text
          fontSize={{ base: "md", md: "lg" }}
          fontWeight="bold"
          bgGradient="linear(to-r, teal.300, cyan.400)"
          bgClip="text"
        >
          🌟 Mattias' Journey 🌟
        </Text>

        {/* Info badges */}
        <HStack spacing={2} flexWrap="wrap" justify="center">
          <Badge
            bg="whiteAlpha.100"
            color="gray.400"
            fontSize={{ base: "2xs", md: "xs" }}
            px={2}
            py={0.5}
            borderRadius="full"
          >
            🎈 Born January 10, 2025
          </Badge>
          <Badge
            bg="whiteAlpha.100"
            color="gray.400"
            fontSize={{ base: "2xs", md: "xs" }}
            px={2}
            py={0.5}
            borderRadius="full"
          >
            📆 {age.totalDays} days alive
          </Badge>
          <Badge
            bg="whiteAlpha.100"
            color="gray.400"
            fontSize={{ base: "2xs", md: "xs" }}
            px={2}
            py={0.5}
            borderRadius="full"
          >
            🗓️ {age.totalWeeks} weeks alive
          </Badge>
          {age.daysUntilBirthday <= 30 && (
            <Badge
              colorScheme="yellow"
              fontSize={{ base: "2xs", md: "xs" }}
              px={2}
              py={0.5}
              borderRadius="full"
            >
              🎂 Birthday in {age.daysUntilBirthday} days!
            </Badge>
          )}
        </HStack>

        {/* Age cards */}
        <HStack spacing={{ base: 2, md: 4 }} justify="center">
          <AgeCard value={age.years} label="Years" emoji="🎂" color="purple" />
          <AgeCard value={age.months} label="Months" emoji="📅" color="teal" />
          <AgeCard value={age.days} label="Days" emoji="🌞" color="cyan" />
        </HStack>

        {/* Fun facts */}
        <SimpleGrid columns={{ base: 3, md: 3 }} spacing={{ base: 2, md: 4 }} w="full" maxW="500px">
          <FunStat
            emoji="🎂"
            value={age.daysUntilBirthday}
            label="to birthday"
            tooltip={`${age.daysUntilBirthday} days until Mattias' next birthday`}
            color="teal"
          />
          <FunStat
            emoji={ZODIAC.symbol}
            value={ZODIAC.name}
            label="zodiac"
            tooltip="Born under the sign of Capricorn"
            color="purple"
          />
          <FunStat
            emoji="📅"
            value={BORN_WEEKDAY}
            label="born on"
            tooltip="Mattias was born on a Friday"
            color="cyan"
          />
        </SimpleGrid>

        {/* Journey to 18 — progresul către ținta de $500/lună */}
        <Box w="full" maxW="500px" pt={1}>
          <HStack justify="space-between" mb={1}>
            <Text fontSize="2xs" color="gray.400" fontWeight="bold" textTransform="uppercase" letterSpacing="wider">
              🎓 Journey to 18
            </Text>
            <Text fontSize="2xs" color="teal.300" fontWeight="bold">
              {age.progressTo18.toFixed(1)}%
            </Text>
          </HStack>
          <Tooltip
            label={`${age.daysUntil18} days left until the $500/month dividend goal`}
            hasArrow
            placement="top"
          >
            <Progress
              value={age.progressTo18}
              size="sm"
              borderRadius="full"
              colorScheme="teal"
              bg="whiteAlpha.100"
              sx={{ "& > div": { transition: "width 0.6s ease" } }}
            />
          </Tooltip>
        </Box>
      </VStack>
    </Box>
  );
};

export default MattiasAgeTracker;